/**
 * Orquestador del envío de correos del PMIS.
 *
 * Punto único que los hooks de mutación llaman después de un save
 * exitoso. Coordina:
 *
 *   1. Lee la `ConfiguracionNotificacion` del handover.
 *      Si NO existe o está deshabilitada → registra `omitido` y sale.
 *   2. Resuelve la lista de correos reales desde la
 *      `ListaDistribucion` cruzando con los destinatarios lógicos
 *      mencionados en el catálogo (`PL`, `JEFE`, …).
 *      Si la lista resuelta es vacía → registra `omitido` y sale.
 *   3. Renderiza el template: si el admin definió `asunto`/`cuerpoTpl`
 *      en la config, los usa con sustitución `{{var}}`; si no, usa el
 *      template registrado en `core/mail/templates`.
 *   4. Llama a `sendMail`. Si tira error, registra `error` con detalle.
 *   5. Si todo sale bien, registra `enviado` con la lista de
 *      destinatarios.
 *
 * **Diseño "fire-and-forget":** la función NUNCA lanza excepciones
 * hacia arriba. Si el correo falla, la mutación que la disparó NO
 * debe revertir — el dato ya quedó guardado y el admin verá el error
 * en la bitácora. Esto desacopla el negocio del envío.
 */

import { buildRepos } from "@core/data/repos";
import { sendMail, MailerNotConfiguredError, MailSendError } from "./graphMailer";
import { renderizarMail } from "./mailRenderer";
import { HANDOVERS_CATALOGO } from "@features/notificaciones/handoversCatalog";
import type { MailContext } from "./mailContext";
import type { EstadoEnvioCorreo } from "@app-types/entities";

export interface NotificarOptions {
  /** Tenant del usuario que disparó la acción. */
  tenantId: string;
  /** ID del handover a notificar (ej. `"PR-2"`, `"A-3"`). */
  handoverId: string;
  /** Contexto a inyectar en el template. */
  context: MailContext;
  /**
   * Lista canónica de destinatarios lógicos extra para este handover.
   * Si se omite, se extraen del texto del catálogo vía heurística.
   */
  destinatariosLogicos?: string[];
}

/**
 * Notifica un handover. Fire-and-forget: NO lanza excepciones.
 * Registra siempre el intento en la bitácora.
 */
export async function notificarHandover(opts: NotificarOptions): Promise<void> {
  const { tenantId, handoverId, context } = opts;
  const repos = buildRepos(tenantId);
  const fechaIntento = new Date().toISOString();
  const entidadRef = `${context.entidad.tipo}:${context.entidad.id}`;

  // Catch-all general: si algo se rompe que no esperábamos, lo
  // registramos como error y seguimos. Nunca propagamos.
  try {
    // 1. Leer config
    const config = await repos.notificacionConfig.getByHandoverId(handoverId);
    if (!config) {
      await registrar(repos, tenantId, {
        handoverId,
        estado: "omitido",
        actor: context.actor.email,
        entidadRef,
        destinatarios: [],
        asunto: "",
        mensajeError: "Sin ConfiguracionNotificacion (handover no inicializado)",
        fechaIntento,
      });
      return;
    }
    if (!config.habilitado) {
      await registrar(repos, tenantId, {
        handoverId,
        estado: "omitido",
        actor: context.actor.email,
        entidadRef,
        destinatarios: [],
        asunto: "",
        mensajeError: "Handover deshabilitado por el admin",
        fechaIntento,
      });
      return;
    }

    // 2. Resolver destinatarios
    const logicos = opts.destinatariosLogicos ?? destinatariosLogicosDesdeCatalogo(handoverId);
    const emails = await resolverEmails(repos, logicos);
    if (emails.length === 0) {
      await registrar(repos, tenantId, {
        handoverId,
        estado: "omitido",
        actor: context.actor.email,
        entidadRef,
        destinatarios: [],
        asunto: "",
        mensajeError: `Sin correos resueltos para destinatarios: ${logicos.join(", ")}`,
        fechaIntento,
      });
      return;
    }

    // 3. Renderizar template (override del admin si existe, si no el default)
    const rendered = renderizarMail(handoverId, context, config.asunto, config.cuerpoTpl);

    // 4. Enviar
    await sendMail({
      to: emails,
      subject: rendered.subject,
      html: rendered.html,
    });

    // 5. Registrar éxito
    await registrar(repos, tenantId, {
      handoverId,
      estado: "enviado",
      actor: context.actor.email,
      entidadRef,
      destinatarios: emails,
      asunto: rendered.subject,
      fechaIntento,
    });
  } catch (err) {
    let mensajeError = "Error desconocido";
    if (err instanceof MailerNotConfiguredError) {
      mensajeError = "Mailer no configurado (modo Dexie local sin MSAL)";
    } else if (err instanceof MailSendError) {
      mensajeError = `MS Graph: ${err.message}${err.statusCode ? ` (${err.statusCode})` : ""}`;
    } else if (err instanceof Error) {
      mensajeError = err.message;
    }
    // Registrar el error pero NO propagar
    try {
      await registrar(repos, tenantId, {
        handoverId,
        estado: "error",
        actor: context.actor.email,
        entidadRef,
        destinatarios: [],
        asunto: "",
        mensajeError,
        fechaIntento,
      });
    } catch {
      // Si ni siquiera podemos registrar el error, lo dejamos en consola.
      console.error("[notificarHandover] no se pudo registrar el error:", err);
    }
  }
}

// ── Helpers internos ─────────────────────────────────────────────────

async function registrar(
  repos: ReturnType<typeof buildRepos>,
  _tenantId: string,
  data: {
    handoverId: string;
    estado: EstadoEnvioCorreo;
    actor: string;
    entidadRef: string;
    destinatarios: string[];
    asunto: string;
    fechaIntento: string;
    mensajeError?: string;
  },
): Promise<void> {
  await repos.notificacionEnviada.create({
    handoverId: data.handoverId,
    estado: data.estado,
    actor: data.actor,
    entidadRef: data.entidadRef,
    destinatarios: data.destinatarios,
    asunto: data.asunto,
    fechaIntento: data.fechaIntento,
    createdBy: data.actor,
    updatedBy: data.actor,
    ...(data.mensajeError ? { mensajeError: data.mensajeError } : {}),
  });
}

async function resolverEmails(
  repos: ReturnType<typeof buildRepos>,
  logicos: string[],
): Promise<string[]> {
  const todos = new Set<string>();
  for (const d of logicos) {
    const emails = await repos.listaDistribucion.resolveEmails(d);
    for (const e of emails) {
      if (e && e.includes("@")) todos.add(e.toLowerCase());
    }
  }
  return Array.from(todos);
}

/**
 * Extrae destinatarios lógicos desde el texto del catálogo. La
 * `pregunta` tiene forma "Notificar a {DEST} cuando...". Buscamos
 * roles canónicos mencionados (PL, JEFE, INT, ABAS, …) en el primer
 * tramo de la frase.
 */
function destinatariosLogicosDesdeCatalogo(handoverId: string): string[] {
  const h = HANDOVERS_CATALOGO.find((x) => x.id === handoverId);
  if (!h) return [];
  const tramo = h.pregunta.split(/\bcuando\b/i)[0] ?? h.pregunta;
  const candidatos = [
    "PL2",
    "back-PL2",
    "back-PL",
    "PL",
    "JEFE",
    "GTE",
    "ABAS lider",
    "ABAS",
    "ADMIN_PMIS",
    "PLANNER",
    "ADOR",
    "INT",
    "back-INT",
    "CFE",
    "FAC",
    "comprador",
    "contratista",
    "proveedor",
    "solicitante",
    "responsable",
    "Contabilidad",
    "Tesoreria",
    "HSE lider",
    "HSE",
    "Ambiental",
    "QA",
    "Seguridad fisica",
    "LEAN",
    "Repositorio conocimiento",
  ];
  const found: string[] = [];
  for (const c of candidatos) {
    const re = new RegExp(`\\b${c.replace(/[-\s]/g, "[-\\s]")}\\b`, "i");
    if (re.test(tramo) && !found.some((f) => f.toLowerCase() === c.toLowerCase())) {
      found.push(c);
    }
  }
  return found;
}

